import {useEffect, useMemo, useState} from 'react';
import {AnimatePresence} from 'framer-motion';
import {useLocation} from 'react-router-dom';
import {useUser} from '../../contexts/UserContext';
import {APP_ROUTES} from '../appShellRoutes';
import type {UniversityVideo} from '../../services/universityService';
import {captureUniversity} from './universityAnalytics';
import useUniversity from './useUniversity';
import UniversityContextTip from './UniversityContextTip';
import UniversityVideoModal from './UniversityVideoModal';
import UniversityWelcomeModal from './UniversityWelcomeModal';

const GETTING_STARTED_LABEL = 'Getting Started';

interface PlayerRequest {
  videos: UniversityVideo[];
  initialIndex: number;
  playlistLabel: string;
  source: string;
}

// ─── App-shell-level Opalite University surfaces ─────────────────────────────
//
// Mounted once inside AppShell so every shell page gets:
//   1. the contextual "Quick video" tip for the current route,
//   2. the one-time guided welcome for /start sign-ins (home only, and only
//      after the facility/onboarding modals have resolved),
//   3. the shared player modal those surfaces open.
// The /learn library and the Home Getting Started card render their own
// player instances; all of them share the module-level catalog store.
const UniversityExperience = () => {
  const location = useLocation();
  const {userData, loading} = useUser();
  const {
    status,
    guided,
    progress,
    videos,
    playlistVideos,
    markWatched,
    dismissVideo,
    markWelcomeSeen,
    acknowledgeGuidedStart,
  } = useUniversity();

  const [player, setPlayer] = useState<PlayerRequest | null>(null);
  // Routes the user already handled a tip on (opened OR dismissed) this
  // session. One tip per page, full stop — without this, acting on a tip
  // recomputes eligibility and the route's NEXT unwatched video pops right
  // up in its place (dismiss "Your Home screen", get "Starting an
  // encounter"), which reads as whack-a-mole.
  const [tipHandledPaths, setTipHandledPaths] = useState<Set<string>>(
    () => new Set(),
  );

  const markTipHandled = (path: string) =>
    setTipHandledPaths(prev => {
      const next = new Set(prev);
      next.add(path);
      return next;
    });

  // The guided welcome must never stack on (or precede) the mandatory
  // facility + onboarding modals, and must never interrupt a live session —
  // it waits for a fully-onboarded user sitting on Home.
  const showWelcome =
    status === 'ready' &&
    guided.showGuidedWelcome &&
    !loading &&
    userData?.onboardingCompleted === true &&
    location.pathname === APP_ROUTES.home &&
    playlistVideos.length > 0;
  const playlistLength = playlistVideos.length;
  const playlistTotalSec = useMemo(
    () => playlistVideos.reduce((sum, video) => sum + video.durationSec, 0),
    [playlistVideos],
  );

  useEffect(() => {
    if (showWelcome) {
      // Clear the browser handoff only after the modal successfully commits.
      // If education UI throws, its safety boundary preserves the app and the
      // same id can be retried without resetting server progress again.
      acknowledgeGuidedStart();
      captureUniversity('university_guided_welcome_shown', {
        playlist_length: playlistLength,
        playlist_total_sec: playlistTotalSec,
      });
    }
  }, [acknowledgeGuidedStart, playlistLength, playlistTotalSec, showWelcome]);

  const tipVideo = useMemo(() => {
    if (status !== 'ready' || showWelcome || player) return null;
    if (tipHandledPaths.has(location.pathname)) return null;
    // While the Getting Started card is educating on Home, a second video
    // surface on the same screen would just be noise.
    if (
      location.pathname === APP_ROUTES.home &&
      guided.showGettingStartedCard
    ) {
      return null;
    }
    return (
      videos.find(
        video =>
          video.contextRoutes.includes(location.pathname) &&
          !progress[video.id]?.watchedAt &&
          !progress[video.id]?.dismissedAt,
      ) ?? null
    );
  }, [
    status,
    showWelcome,
    player,
    location.pathname,
    tipHandledPaths,
    guided.showGettingStartedCard,
    videos,
    progress,
  ]);

  useEffect(() => {
    if (tipVideo) {
      captureUniversity('university_tip_shown', {
        video_id: tipVideo.id,
        video_title: tipVideo.title,
        duration_sec: tipVideo.durationSec,
        route: location.pathname,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tipVideo?.id]);

  const handleTipOpen = (video: UniversityVideo) => {
    captureUniversity('university_tip_opened', {
      video_id: video.id,
      video_title: video.title,
      duration_sec: video.durationSec,
      route: location.pathname,
    });
    markTipHandled(location.pathname);
    setPlayer({
      videos: [video],
      initialIndex: 0,
      playlistLabel: '',
      source: 'context_tip',
    });
  };

  const handleTipDismiss = (video: UniversityVideo) => {
    captureUniversity('university_tip_dismissed', {
      video_id: video.id,
      video_title: video.title,
      duration_sec: video.durationSec,
      route: location.pathname,
    });
    markTipHandled(location.pathname);
    dismissVideo(video.id);
  };

  const handleWelcomeStart = () => {
    captureUniversity('university_guided_welcome_started', {
      playlist_length: playlistVideos.length,
      dismiss_method: 'start_watching',
    });
    markWelcomeSeen();
    setPlayer({
      videos: playlistVideos,
      initialIndex: 0,
      playlistLabel: GETTING_STARTED_LABEL,
      source: 'guided_welcome',
    });
  };

  const handleWelcomeSkip = (dismissMethod: 'skip_button' | 'close') => {
    captureUniversity('university_guided_welcome_skipped', {
      playlist_length: playlistVideos.length,
      dismiss_method: dismissMethod,
    });
    markWelcomeSeen();
  };

  return (
    <>
      <AnimatePresence>
        {tipVideo && (
          <UniversityContextTip
            key={tipVideo.id}
            video={tipVideo}
            onOpen={handleTipOpen}
            onDismiss={handleTipDismiss}
          />
        )}
      </AnimatePresence>

      <UniversityWelcomeModal
        isOpen={showWelcome}
        playlistVideos={playlistVideos}
        onStart={handleWelcomeStart}
        onSkip={handleWelcomeSkip}
      />

      {player && (
        <UniversityVideoModal
          isOpen
          onClose={() => setPlayer(null)}
          videos={player.videos}
          initialIndex={player.initialIndex}
          playlistLabel={player.playlistLabel}
          source={player.source}
          onWatched={markWatched}
        />
      )}
    </>
  );
};

export default UniversityExperience;
